import { z } from 'zod';
import { type Tasks } from '../../../workflow/schemaTypes';

export const COMMENT_MAX_LENGTH = 250;

export type Comment = {
  value?: string;
  metadataId?: string;
};

export type Photo = {
  id: string;
  previewUrl: string;
  metadataId?: string;
};

export type ReasonItemData = {
  reasonId?: string;
  nestedReasonId?: string;
  comments?: Comment;
  isCommentRequired?: string;
  isNestedReasonRequired?: string;
  isPhotoRequired?: string;
  attestation?: { checked?: boolean; metadataId?: string };
  isAttestationRequired?: boolean;
  photos?: Photo[];
};

export type ReasonData = Record<Extract<Tasks, 'reason'>, ReasonItemData>;

export const ReasonsTaskSchema = z
  .object({
    reasonId: z.string({
      error: (issue) =>
        issue.input === undefined ? 'Selection required' : 'Selection required',
    }),
    nestedReasonId: z
      .string({
        error: (issue) =>
          issue.input === undefined
            ? 'Selection required'
            : 'Selection required',
      })
      .optional(),
    isNestedReasonRequired: z.enum(['true', 'false']).optional(),
    comments: z
      .object({
        value: z
          .string({
            error: (issue) =>
              issue.input === undefined
                ? 'Comment required'
                : 'Comment required',
          })
          .max(COMMENT_MAX_LENGTH, {
            message: `Comment too long, maximum ${COMMENT_MAX_LENGTH} characters`,
          })
          .optional(),
        metadataId: z.string().optional(),
      })
      .optional(),
    isCommentRequired: z.enum(['true', 'false']).optional(),
    isPhotoRequired: z.enum(['true', 'false']).optional(),
    attestation: z
      .object({
        checked: z.boolean().optional(),
        metadataId: z.string().optional(),
      })
      .optional(),
    isAttestationRequired: z.boolean().optional(),
    photos: z
      .array(
        z.object({
          id: z.string({
            error: (issue) =>
              issue.input === undefined ? 'Photo required' : 'Photo Required',
          }),
          metadataId: z.string().optional(),
          previewUrl: z.string().optional(),
        }),
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    const isNestedReasonRequired = data.isNestedReasonRequired === 'true';
    if (data.reasonId) {
      if (isNestedReasonRequired && !data.nestedReasonId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Missing nested reason',
        });
      }
    }

    const shouldValidateFields = Boolean(
      isNestedReasonRequired
        ? data.reasonId && data.nestedReasonId
        : data.reasonId,
    );

    if (
      shouldValidateFields &&
      data.isCommentRequired === 'true' &&
      (!data.comments?.value || data.comments.value.length < 1)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Missing comments',
      });
    }

    if (
      shouldValidateFields &&
      data.isPhotoRequired === 'true' &&
      (!data.photos || data.photos.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Missing photo',
      });
    }

    if (
      shouldValidateFields &&
      data.isAttestationRequired === true &&
      data.attestation?.checked !== true
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Missing attestation',
      });
    }
  });

export type ReasonsTaskSchemaType = z.infer<typeof ReasonsTaskSchema>;
